import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Layout from "antd/es/layout";
import notification from "antd/es/notification";
import Row from "antd/es/row";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import headerBanner from "@app/assets/reports/header-panel.svg";
import rightSideBanner from "@app/assets/reports/scorecard.jpg";
import TextWithLine from "@app/component/typography/TextWithLine";
import { getMe, getUserSettingValue } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import Iam from "@app/types/Iam";
import WeeklyReportSetting from "@app/types/report/WeeklyReportCardSettings";

import { reportsWeeklyCardAnalytics } from "../../reportsAnalytics";
import { saveWeeklyReport } from "../../store/actions";
import { isSavingWeeklyReport, isStackRankAvailable } from "../../store/selectors";

import ActionItems from "./components/ActionItems";
import ButtonBar from "./components/ButtonBar";
import OptInOut from "./components/OptInOut";
import QuickFigures from "./components/QuickFigures";
import StackRankReport from "./components/StackRankReport";
import TeamLeaderBoard from "./components/TeamLeaderboard";
import isWeeklyReportSettingEqual from "./utils/isWeeklyReportSettingEqual";
import styles from "./WeeklyReports.module.scss";

const messages = defineMessages({
  noQuickFigures: {
    id: "reports.weeklyReportsCard.validation.noQuickFigures",
    defaultMessage: "Please select quick figure metric to display in mail",
    description: "Validation on Weekly Reports page to choose at least one quick figure",
  },
  noTeamLeaderboard: {
    id: "reports.weeklyReportsCard.notification.noTeamLeaderboard",
    defaultMessage: "Please select team leaderboard metric to display in mail",
    description: "Validation on Weekly Reports page to choose team leaderboard metric",
  },
});

interface Props {
  me: Iam;
  saveWeeklyReport: typeof saveWeeklyReport.request;
  saving?: boolean;
  stackRankVisibility: boolean;
  weeklyReport: WeeklyReportSetting["value"];
}

const WeeklyReportsCard: React.FC<Props> = ({
  me,
  saveWeeklyReport,
  saving,
  stackRankVisibility,
  weeklyReport,
}) => {
  const intl = useIntl();

  const [formData, setFormData] = useState<WeeklyReportSetting["value"]>(weeklyReport);
  useEffect(() => {
    setFormData(weeklyReport);
  }, [setFormData, weeklyReport]);

  const isChanged = useMemo(
    () => !isWeeklyReportSettingEqual(weeklyReport, formData),
    [weeklyReport, formData]
  );

  const handleSubmit = useCallback(() => {
    if (formData.quickFigures.enabled && formData.quickFigures.config.length === 0) {
      notification.error({ message: intl.formatMessage(messages.noQuickFigures) });
    } else if (formData.teamLeaderboard.enabled && formData.teamLeaderboard.config.length === 0) {
      notification.error({ message: intl.formatMessage(messages.noTeamLeaderboard) });
    } else {
      saveWeeklyReport(formData);
      reportsWeeklyCardAnalytics.clicked(["Save Report Preferences "]);
    }
  }, [formData, intl, saveWeeklyReport]);

  const handleCancel = useCallback(() => {
    setFormData(weeklyReport);
    reportsWeeklyCardAnalytics.clicked(["Revert Changes"]);
  }, [setFormData, weeklyReport]);

  return (
    <section className={styles.mainContainer}>
      <Layout className={styles.container}>
        <Layout.Content className={styles.content}>
          <Row>
            <Col span={24}>
              <img alt="Weekly Report Header Banner" src={headerBanner} width="100%" />
            </Col>
          </Row>
          <Row className={styles.reportFiguresContainer}>
            <Col span={16}>
              <OptInOut formData={formData} />
              <TextWithLine>
                <FormattedMessage
                  id="reports.weeklyReportCard.reportControlsTitle"
                  defaultMessage="Control what goes into your report"
                  description="Title for Weekly Reports Section"
                />
              </TextWithLine>
              <QuickFigures formData={formData} onChange={setFormData} />
              <ActionItems formData={formData} onChange={setFormData} />
              {stackRankVisibility && (
                <>
                  <TeamLeaderBoard formData={formData} me={me} onChange={setFormData} />
                  <StackRankReport formData={formData} onChange={setFormData} />
                </>
              )}
            </Col>
            <Col className={styles.sampleEmailContainer} span={6}>
              <img alt="Weekly Report Email Example" src={rightSideBanner} width="100%" />
            </Col>
          </Row>
        </Layout.Content>
      </Layout>
      {isChanged && (
        <footer className={styles.footerContainer}>
          <div className={styles.footer}>
            <ButtonBar
              nextLoading={saving}
              onCancelClick={handleCancel}
              onNextClick={handleSubmit}
            />
          </div>
        </footer>
      )}
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  me: getMe(state)!,
  saving: isSavingWeeklyReport(state),
  stackRankVisibility: isStackRankAvailable(state),
  weeklyReport: getUserSettingValue(state)("weeklyReport"),
});

const mapDispatchToProps = {
  saveWeeklyReport: saveWeeklyReport.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(WeeklyReportsCard);
